/*
  Haptics util (HAP-1886, parent HAP-1884 Phase 0.2).

  Thin wrapper over the Vibration API (`navigator.vibrate`) with a capability
  check so it safely NO-OPS where unsupported — notably iOS Safari, which has
  no Vibration API at all. Fires short pulses (10–20ms) for key tap actions on
  capable devices (Android Chrome/Firefox).

  Usage:
    import { useHaptics } from '@/composables/useHaptics'   // or relative path
    const haptics = useHaptics()
    haptics.light()                 // ~10ms — taps, toggles, nav
    haptics.medium()                // ~20ms — confirms, primary actions
    haptics.success() / .error()    // short patterns for feedback

  Or declaratively via the globally-registered `v-haptic` directive (see
  main.js):  <button v-haptic>…</button>  /  <button v-haptic:medium>…</button>
*/

// Capability check. Guard `navigator` for SSR (entry-server.js); `vibrate` is
// absent on iOS Safari and desktop Safari, so this is false there → no-op.
export const hapticsSupported = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'

// Named pulse patterns (ms). Numbers = single buzz; arrays = buzz/pause/buzz…
export const HAPTIC_PATTERNS = {
	light: 10,
	medium: 20,
	heavy: 30,
	success: [10, 40, 15],
	error: [20, 50, 20],
}

function fire(pattern) {
	if (!hapticsSupported) return false
	try {
		return navigator.vibrate(pattern)
	} catch (e) {
		// Some browsers throw if called outside a user gesture or when disabled.
		return false
	}
}

export function useHaptics() {
	return {
		supported: hapticsSupported,
		// Raw escape hatch — accepts a number or pattern array.
		vibrate: (pattern = HAPTIC_PATTERNS.light) => fire(pattern),
		// Named helpers for the common cases.
		light: () => fire(HAPTIC_PATTERNS.light),
		medium: () => fire(HAPTIC_PATTERNS.medium),
		heavy: () => fire(HAPTIC_PATTERNS.heavy),
		success: () => fire(HAPTIC_PATTERNS.success),
		error: () => fire(HAPTIC_PATTERNS.error),
		// Stop any ongoing vibration.
		stop: () => fire(0),
	}
}

export default useHaptics
